import React, { FC, useEffect } from "react";
import styled from "styled-components";
import SvgX from "../Icons/X";
import useContentful from "../../hooks/useContentful";
import contentfulComponentLoop from "../../lib/contentfulComponentLoop";
import getContentfulLocale from "../../lib/getContentfulLocale";
import Loader from "../Loader";

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  z-index: 1501;
`;

const ModalContainer = styled.div`
  width: 100%;
  margin: 0 auto;
`;

interface ModalStyleProps {
  modalMaxWidth?: string;
  modalMaxWidthMobile?: string;
  removeModalPadding?: boolean;
}

const ModalWrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => !["removeModalPadding"].includes(prop),
})<ModalStyleProps>`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1502;
  background: #fff;
  border-radius: 8px;
  overflow: auto;
  min-width: 320px;
  width: 90%;
  max-width: ${({ modalMaxWidth }) => modalMaxWidth || "90%"};
  max-height: 90%;
  padding: ${({ removeModalPadding }) =>
    removeModalPadding ? "0" : "1rem"};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    max-width: ${({ modalMaxWidthMobile }) =>
      modalMaxWidthMobile || "90%"};
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 1503;

  svg {
    width: 24px;
    height: 24px;
    fill: #000;
  }
`;

declare global {
  interface Window {
    activeModalID: string;
  }
}

interface MainModalContentProps {
  onClose: () => void;
  htmlContent?: string;
}

const MainModalContent: FC<MainModalContentProps> = ({
  onClose,
  htmlContent,
}) => {
useEffect(() => {

  const scrollY = window.scrollY || document.documentElement.scrollTop;
  const appRoot = document.getElementById("basic-modal-entry");
  if (appRoot) {
    Object.assign(appRoot.style, {
      position: "fixed",
      top: `-${scrollY}px`,
      left: "0",
      right: "0",
      width: "100%",
      zIndex: "9999",
    });
  }
  document.body.style.overflow = "hidden";
  return () => {
    document.body.style.overflow = "";
    if (appRoot) {
      Object.assign(appRoot.style, {
        position: "",
        top: "",
        left: "",
        right: "",
        width: "",
        zIndex: "",
      });
    }
    window.scrollTo(0, scrollY);
  };
}, []);

  if (htmlContent) {
    return (
      <>
        <Backdrop onClick={onClose} />
        <ModalWrapper
          modalMaxWidth="750px"
          modalMaxWidthMobile="100%"
          removeModalPadding={false}
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton onClick={onClose}>
            <SvgX />
          </CloseButton>
          <ModalContainer
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        </ModalWrapper>
      </>
    );
  }

  const localeString =
    document
      .getElementById("basic-modal-entry")
      ?.getAttribute("data-shop-locale") || "en";
  const localeMarket =
    document
      .getElementById("basic-modal-entry")
      ?.getAttribute("data-shop-market") || "us";
  const locale = getContentfulLocale(localeString, localeMarket);

  if (window.activeModalID === "modshop-redirect-modal") {
    return <>you do not have access to this page</>;
  }

  const { contentfulData }: any = useContentful({
    include: 10,
    "fields.modalId": window.activeModalID,
    content_type: "modal",
    locale,
  });

  const item = contentfulData?.items[0]?.fields || {};
  const modalMaxWidth = item.modalMaxWidth
    ? `${item.modalMaxWidth}px`
    : "750px";
  const modalMaxWidthMobile = item.modalMaxWidthMobile
    ? `${item.modalMaxWidthMobile}px`
    : "100%";
  const removeModalPadding = item.removeModalPadding || false;

  if (!contentfulData) {
    return (
      <>
        <Backdrop onClick={onClose} />
        <ModalWrapper
          modalMaxWidth={modalMaxWidth}
          modalMaxWidthMobile={modalMaxWidthMobile}
          removeModalPadding={removeModalPadding}
          onClick={(e) => e.stopPropagation()}
        >
          <Loader />
        </ModalWrapper>
      </>
    );
  }

  return (
    <>
      <Backdrop onClick={onClose} />
      <ModalWrapper
        modalMaxWidth={modalMaxWidth}
        modalMaxWidthMobile={modalMaxWidthMobile}
        removeModalPadding={removeModalPadding}
        onClick={(e) => e.stopPropagation()}
      >
        <CloseButton onClick={onClose}>
          <SvgX />
        </CloseButton>
        <ModalContainer>
          {contentfulComponentLoop(
            contentfulData.items[0].fields.components,
            { locale }
          )}
        </ModalContainer>
      </ModalWrapper>
    </>
  );
};

export default MainModalContent;
